import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { Absorber } from "../../../../../../http/vertigrip/types/absorbers";
import { useAbsorbersApi } from "../../../../../../http/vertigrip/useAbsorbers";
import { PaginationQuery } from "../../../../../../types";
import FlexContent from "../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import CreateAbsorberModalDialogButton from "./CreateAbsorberModalDialogButton";
import EditAbsorberModalDialog from "./EditAbsorberModalDialog";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Absorbers: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { steelVersionId } = useParams<{ steelVersionId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "steelVersionAbsorbers" });
  const aborter = useRef(new AbortController());
  const absorbersApi = useAbsorbersApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "steelVersionAbsorbers");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await absorbersApi.updateAdminAbsorberVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (absorberId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await absorbersApi.updateAdminAbsorberOrder(absorberId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<Absorber>();
  const [itemToDelete, setItemToDelete] = useState<Absorber>();
  const { accessesAction, accessesDialog } = useEntityAccesses<Absorber>("vertigrip.absorber");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = absorbersApi.useAdminSteelVersionAbsorbers(steelVersionId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.absorbers.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.absorbers.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateAbsorberModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<Absorber>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.absorbers.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                absorbersApi
                  .deleteAdminAbsorber(itemToDelete.id, itemToDelete.vertigripSteelVersion.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.absorbers.questions.delete}
        </ModalDialog>
      )}
      <EditAbsorberModalDialog onClose={() => setItemToEdit(undefined)} absorber={itemToEdit} />
    </FlexContent>
  );
};

export default Absorbers;
